import { FunctionComponent } from "preact";
import { useEffect, useState, useMemo } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation, useParams } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined } from "../../store/user_info.ts";
import { gotoLoginPage } from "../../router_utils.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { PhotoGrid } from "../../components/photo_grid/photo_grid.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { $cameraList, refreshCameraPage } from "../../store/cameras.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";

export const PhotoManagerPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const params = useParams();
    const isLogined = useStore($isLogined);
    const cameraList = useStore($cameraList);
    const [location, _setLocation] = useLocation();
    const refresh = usePageRefresh({ pageKey: location, enabled: isLogined, refresh: refreshCameraPage });
    
    // Default to first camera if available and not specified (though router usually requires ID)
    const cameraIdParam = Number.parseInt(params["cameraId"] ?? "0");
    const [selectedCameraId, setSelectedCameraId] = useState(cameraIdParam);

    useEffect(() => {
        document.title = t({ zh: "照片管理", en: "Photo Manager" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? `/photos/${selectedCameraId}`);
        }
    }, [isLogined, location, selectedCameraId]);
    
    // Sync local state if param changes
    useEffect(() => {
        if (cameraIdParam > 0) {
            setSelectedCameraId(cameraIdParam);
        } else if (cameraList.length > 0 && selectedCameraId === 0) {
            // Auto-select first camera if none selected
             setSelectedCameraId(cameraList[0].camera_id);
        }
    }, [cameraIdParam, cameraList]);

    const currentCameraName = useMemo(() => {
        const cam = cameraList.find(c => c.camera_id === selectedCameraId);
        return cam ? cam.camera_name : `Camera ${selectedCameraId}`;
    }, [cameraList, selectedCameraId]);

    if (!isLogined) return <article aria-busy="true" class="container"></article>;

    return (
        <div class="farpov_top_container" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {refresh.error && <p role="alert">{refresh.error}</p>}
            <PageHeader 
                title={t({ zh: "照片管理", en: "Pic" })} 
                subtitle={selectedCameraId > 0 ? t({ zh: `正在查看: ${currentCameraName}`, en: `Viewing: ${currentCameraName}` }) : ""}
                class="farpov_photo_manager_header"
            />
            
            <div style={{ flex: 1, minHeight: 0 }}>
                {selectedCameraId > 0 ? (
                    <PhotoGrid cameraId={selectedCameraId} />
                ) : (
                    <div class="farpov_data_grid_empty">
                        <Icon name="camera" />
                        <p>{t({ zh: "请选择一个相机", en: "Please select a camera" })}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PhotoManagerPage;
